import { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Icon } from '@/components/ui/icon';
import { useClientTranslation } from '@/lib/core/i18n';
import type { ProductSortKey, ProductTagFacetDto } from '@/services/platform/api/model';
import { SheetHeader } from '@/components/shared/shop/ui/SheetHeader';
import { FilterCheckboxItem } from './checkbox-item';
import { FilterBottomBar } from './bottom-bar';
import { FilterPriceRange } from './price-range';
import { FilterSortBy } from './sort-by';

export interface ShopProductListFilterSheetFilters {
  sortBy?: ProductSortKey;
  brands: string[];
  colorSlugs: string[];
  priceRange: [number, number];
}

export interface ShopProductListFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sortBy?: ProductSortKey;
  brands: string[];
  colors: ProductTagFacetDto[];
  minPrice: number;
  maxPrice: number;
  /** When set (a brand-scoped page), the brand checkboxes are disabled and this brand can't be removed. */
  lockedBrand?: string;
  selectedBrands: string[];
  selectedColorSlugs: string[];
  priceRange: [number, number];
  onApply: (filters: ShopProductListFilterSheetFilters) => void;
  /** Number of products currently loaded for the applied filters. */
  resultCount: number;
  /** Whether a products fetch (triggered by this or any other filter change) is in flight. */
  isLoadingResults: boolean;
}

export function ShopProductListFilterSheet({
  open,
  onOpenChange,
  sortBy,
  brands,
  colors,
  minPrice,
  maxPrice,
  lockedBrand,
  selectedBrands,
  selectedColorSlugs,
  priceRange,
  onApply,
  resultCount,
  isLoadingResults,
}: ShopProductListFilterSheetProps) {
  const t = useClientTranslation();

  // Draft state mirrors the committed selection and is reset when the sheet (re)opens.
  const [draftSortBy, setDraftSortBy] = useState<ProductSortKey | undefined>(sortBy);
  const [draftBrands, setDraftBrands] = useState<string[]>(selectedBrands);
  const [draftColorSlugs, setDraftColorSlugs] = useState<string[]>(selectedColorSlugs);
  const [draftPriceRange, setDraftPriceRange] = useState<[number, number]>(priceRange);
  // Tracks the outcome of the last "Show results" tap — the sheet only auto-closes
  // once the resulting fetch confirms there are products to show. 'submitted' waits
  // for the apply-triggered fetch to actually start (isLoadingResults may still be
  // reporting the *previous* fetch's stale state for one render right after onApply)
  // before 'loading' starts watching for it to finish.
  const [applyPhase, setApplyPhase] = useState<'idle' | 'submitted' | 'loading'>('idle');
  const [noResultsForDraft, setNoResultsForDraft] = useState(false);
  // Tracks whether the sheet was already open on the previous render — the drafts
  // must only reset on the open transition (a fresh open), not every time `sortBy`/
  // `selectedBrands`/etc change while already open, which happens right after our
  // own "Show results" tap commits its filters (and would otherwise stomp `applyPhase`
  // back to 'idle' before it can ever observe the resulting fetch).
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      setDraftSortBy(sortBy);
      setDraftBrands(selectedBrands);
      setDraftColorSlugs(selectedColorSlugs);
      setDraftPriceRange(priceRange);
      setApplyPhase('idle');
      setNoResultsForDraft(false);
    }
  }, [open, sortBy, selectedBrands, selectedColorSlugs, priceRange]);

  useEffect(() => {
    if (applyPhase === 'submitted' && isLoadingResults) {
      setApplyPhase('loading');
    } else if (applyPhase === 'loading' && !isLoadingResults) {
      setApplyPhase('idle');
      if (resultCount > 0) {
        onOpenChange(false);
      } else {
        setNoResultsForDraft(true);
      }
    }
  }, [applyPhase, isLoadingResults, resultCount, onOpenChange]);

  const toggleBrand = useCallback(
    (name: string) => {
      if (name === lockedBrand) return;
      setDraftBrands(prev => (prev.includes(name) ? prev.filter(b => b !== name) : [...prev, name]));
      setNoResultsForDraft(false);
    },
    [lockedBrand]
  );

  const toggleColor = useCallback((slug: string) => {
    setDraftColorSlugs(prev => (prev.includes(slug) ? prev.filter(c => c !== slug) : [...prev, slug]));
    setNoResultsForDraft(false);
  }, []);

  const handleSortByChange = useCallback((value: ProductSortKey) => {
    setDraftSortBy(value);
    setNoResultsForDraft(false);
  }, []);

  const handlePriceRangeChange = useCallback((value: [number, number]) => {
    setDraftPriceRange(value);
    setNoResultsForDraft(false);
  }, []);

  const isPriceRangeChanged = draftPriceRange[0] !== minPrice || draftPriceRange[1] !== maxPrice;

  const handleShowResults = useCallback(() => {
    onApply({
      sortBy: draftSortBy,
      brands: draftBrands,
      colorSlugs: draftColorSlugs,
      priceRange: draftPriceRange,
    });
    setNoResultsForDraft(false);
    setApplyPhase('submitted');
  }, [draftSortBy, draftBrands, draftColorSlugs, draftPriceRange, onApply]);

  const handleRemoveAll = useCallback(() => {
    setDraftSortBy(undefined);
    setDraftBrands(lockedBrand ? [lockedBrand] : []);
    setDraftColorSlugs([]);
    setDraftPriceRange([minPrice, maxPrice]);
    setNoResultsForDraft(false);
  }, [lockedBrand, minPrice, maxPrice]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="flex h-[585px] max-h-[85dvh] flex-col overflow-hidden">
        <SheetHeader
          title={t('App.Shop.Filter.Title')}
          right={
            <button
              type="button"
              aria-label={t('App.Labels.Close')}
              onClick={handleClose}
              className="flex size-11 cursor-pointer items-center justify-center"
            >
              <Icon name="xmark" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          }
        />

        {/* Body */}
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto px-4 pb-4">
          <Accordion type="multiple" className="border-jncquoi-neutral-900 border-b-[0.5px]">
            {/* SORT BY — single-select radio list */}
            <AccordionItem value="sort">
              <AccordionTrigger>{t('App.Shop.Filter.SortBy')}</AccordionTrigger>
              <AccordionContent>
                <FilterSortBy value={draftSortBy} onChange={handleSortByChange} />
              </AccordionContent>
            </AccordionItem>

            {/* BRANDS — 2-col checkbox grid. Locked (and not expandable) on a brand-scoped page. */}
            <AccordionItem value="brands" disabled={Boolean(lockedBrand)}>
              <AccordionTrigger>{t('App.Shop.Filter.Brands')}</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 pt-2 pb-6">
                  {brands.map(brand => (
                    <FilterCheckboxItem
                      key={brand}
                      id={`filter-brand-${brand}`}
                      label={brand}
                      checked={draftBrands.includes(brand)}
                      onChange={() => toggleBrand(brand)}
                      disabled={brand === lockedBrand}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* COLOR — 2-col checkbox grid */}
            <AccordionItem value="color">
              <AccordionTrigger>{t('App.Shop.Filter.Color')}</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 pt-2 pb-6">
                  {colors.map(color => {
                    const slug = color.slug ?? color.name ?? '';
                    return (
                      <FilterCheckboxItem
                        key={slug}
                        id={`filter-color-${slug}`}
                        label={color.name ?? ''}
                        checked={draftColorSlugs.includes(slug)}
                        onChange={() => toggleColor(slug)}
                      />
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* PRICE — dual-thumb range slider */}
            <AccordionItem value="price">
              <AccordionTrigger>{t('App.Shop.Filter.Price')}</AccordionTrigger>
              <AccordionContent>
                <FilterPriceRange
                  min={minPrice}
                  max={maxPrice}
                  value={draftPriceRange}
                  onChange={handlePriceRangeChange}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <FilterBottomBar
          selectedCount={
            (draftSortBy ? 1 : 0) +
            draftBrands.filter(b => b !== lockedBrand).length +
            draftColorSlugs.length +
            (isPriceRangeChanged ? 1 : 0)
          }
          onShowResults={handleShowResults}
          onRemoveAll={handleRemoveAll}
          noResults={noResultsForDraft}
          pending={applyPhase !== 'idle'}
        />
      </DrawerContent>
    </Drawer>
  );
}
